// Redux
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setSuggestionsSearchResults } from "redux/searchBar/searchBarSlice";
import { setEndedCourse } from "redux/showResults/showResultsSlice";

// Types
import type { FilterSlice, RootState } from "redux/types";
import {Category, CurrentSelectedCategory } from "interfaces/category";

// Utils
import { getParameterForOsaQuery } from "utils/osaSearchUtil";
import { fetchAvailableFiltersLastInteraction, handleInvalidTags } from "./utils";
import { filterQueryStringByAllowedParams } from "utils/queryString";

// Services
import { commonOsaQuery, getOsaContext } from "services/osaSearch/osaSearch";

// Constants
import { defaultResultsQuery, NUMBER_ELEMENTS_PER_PAGE } from "constants/results";
import { Layout } from "constants/layout";
import { ProductState } from "constants/gpoSearchEnum";
import { QueryStringPrimaryKeys } from "constants/urlEnum";

// Defaults
export const initialState: FilterSlice = {
	activeFilters: [],
	urlPath: "",
	numberElementsPerPage: NUMBER_ELEMENTS_PER_PAGE,
	productsVisibility: Layout.LIST,
	isOpenMobileSidebar: false,
	availableFilters: [],
	sortedSelectedFilters: [],
	lastFilterAction: null
};

// Thunks
export const setActiveFiltersAndUrlPath = createAsyncThunk<
	{ pathname: string; filters: CurrentSelectedCategory[]; numberElementsPerPage: number },
	string,
	{ rejectValue: unknown; state: RootState }
>("SET_ACTIVE_FILTERS_AND_URL_PATH", async (pathname, { rejectWithValue, dispatch, getState }) => {
	try {
		const state = getState();
		const { categories } = state.categories;
		const { sortedSelectedFilters, availableFilters: previousAvailableFilters, lastFilterAction } = state.filters;

		const { querySearch, gpoFilters, gpoSorting, paging, productPerPage, selectedCategories } = getParameterForOsaQuery(pathname, categories);

		if(selectedCategories.length === 0){
			dispatch(setSortedSelectedFilters([]));
		}

		let updatedSelectedCategories: CurrentSelectedCategory[] = selectedCategories;
		const allowedTagParameters = categories.map(cat => cat.urlParameter);
		const allowedSimpleParameters = Object.values(QueryStringPrimaryKeys);

		const res = await commonOsaQuery(querySearch, gpoFilters, gpoSorting, paging, productPerPage, categories);

		if (res) {
			dispatch(setSuggestionsSearchResults(res));
			dispatch(setEndedCourse(res.results.filter((result) =>
				result.grouping === ProductState.NOTSTARTED_NOTVALID ||
				result.grouping === ProductState.STARTED_ENDED ||
				result.grouping === ProductState.ELEARNING_NOTVALID
			)));

			const osaContext = getOsaContext();
			const lastCategorySelected = sortedSelectedFilters.length > 0 ? sortedSelectedFilters[sortedSelectedFilters.length - 1] : null;
			const availableFiltersFromResults = res.tags;

			const availableFilters = await fetchAvailableFiltersLastInteraction(
				osaContext,
				gpoFilters,
				categories,
				querySearch,
				lastCategorySelected,
				availableFiltersFromResults,
				lastFilterAction,
				previousAvailableFilters);

			const handleInvalidTagsResult = handleInvalidTags(sortedSelectedFilters, availableFilters, pathname, selectedCategories, categories);
			const { newSelectedCategories, newSortedSelectedFilters, newPathname, hasInvalidTags } = handleInvalidTagsResult;
			if(hasInvalidTags){
				updatedSelectedCategories = newSelectedCategories;
				dispatch(setSortedSelectedFilters(newSortedSelectedFilters));
				pathname = newPathname;
			}

			dispatch(setAvailableFilters(availableFilters));

			pathname = filterQueryStringByAllowedParams(pathname, allowedTagParameters, allowedSimpleParameters);
		} else {
			dispatch(setSuggestionsSearchResults(defaultResultsQuery));
			dispatch(setEndedCourse([]));
			dispatch(setAvailableFilters([]));

			pathname = filterQueryStringByAllowedParams(pathname, allowedTagParameters, allowedSimpleParameters);
		}
		return { pathname, filters: updatedSelectedCategories, numberElementsPerPage: productPerPage };
	} catch (error) {
		return rejectWithValue(error);
	}
});

// Slice
export const filtersSlice = createSlice({
	name: "FILTERS",
	initialState,
	reducers: {
		setActiveFilters: (state, action: PayloadAction<CurrentSelectedCategory[]>) => {
			return { ...state, activeFilters: action.payload };
		},
		setUrlPath: (state, action: PayloadAction<string>) => {
			return { ...state, urlPath: action.payload };
		},
		setNumberPerPage: (state, action: PayloadAction<number>) => {
			return { ...state, numberElementsPerPage: action.payload };
		},
		setProductsVisibility: (state, action: PayloadAction<Layout>) => {
			return { ...state, productsVisibility: action.payload };
		},
		setOpenMobileSidebar: (state, action: PayloadAction<boolean>) => {
			return { ...state, isOpenMobileSidebar: action.payload };
		},
		setAvailableFilters: (state, action: PayloadAction<Category[]>) => {
			return { ...state, availableFilters: action.payload };
		},
		setSortedSelectedFilters: (state, action: PayloadAction<Category[]>) => {
			return { ...state, sortedSelectedFilters: action.payload };
		},
		addSortedSelectedFilter(state, action: PayloadAction<Category>) {
			const exists = state.sortedSelectedFilters.some((filter) => filter.id === action.payload.id);
			if (!exists) {
				state.sortedSelectedFilters.push(action.payload);
				state.lastFilterAction = "added";
			}
		},
		removeSortedSelectedFilter(state, action: PayloadAction<Category>) {
			const beforeLength = state.sortedSelectedFilters.length;
			state.sortedSelectedFilters = state.sortedSelectedFilters.filter((filter) => filter.id !== action.payload.id);
			if (state.sortedSelectedFilters.length < beforeLength) {
				state.lastFilterAction = "removed";
			}
		}
	},
	extraReducers(builder) {
		builder.addCase(setActiveFiltersAndUrlPath.fulfilled, (state, { payload }) => {
			const { pathname, filters, numberElementsPerPage } = payload;
			state.activeFilters = filters;
			state.urlPath = pathname;
			state.numberElementsPerPage = numberElementsPerPage;
		});
	}
});

// Actions
export const { setActiveFilters,
	setUrlPath,
	setNumberPerPage,
	setProductsVisibility,
	setOpenMobileSidebar,
	setAvailableFilters,
	setSortedSelectedFilters,
	addSortedSelectedFilter,
	removeSortedSelectedFilter} = filtersSlice.actions;

// Selector
export const activeFiltersSelector = (state: RootState): CurrentSelectedCategory[] => state.filters.activeFilters;
export const urlPathSelector = (state: RootState): string => state.filters.urlPath;
export const numberElementsPerPageSelector = (state: RootState): number => state.filters.numberElementsPerPage;
export const productVisibilitySelector = (state: RootState): Layout => state.filters.productsVisibility;
export const isOpenMobileSidebarSelector = (state: RootState): boolean => state.filters.isOpenMobileSidebar;
export const availableFiltersSelector = (state: RootState): Category[] => state.filters.availableFilters;
export const sortedSelectedFiltersSelector = (state: RootState): Category[] => state.filters.sortedSelectedFilters;

// Reducers
export default filtersSlice.reducer;
